// Google Analytics 4 (GA4) configuration
// Set REACT_APP_GA_MEASUREMENT_ID to the measurement ID. Without it nothing
// loads at all, which is the intended state until analytics is actually wanted.
import { isStorageAllowed } from '../shared/utils/consentStorage';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

if (!GA_MEASUREMENT_ID && process.env.NODE_ENV === 'development') {
  console.warn('Google Analytics Measurement ID is not set. Please set REACT_APP_GA_MEASUREMENT_ID environment variable.');
}

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window === 'undefined' || window.gtag || !GA_MEASUREMENT_ID) {
    return; // Already initialized, server-side, or no ID configured
  }

  // Create dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(...args: any[]) {
    window.dataLayer!.push(args);
  };

  // Load GA script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Configure GA
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll track page views manually
    anonymize_ip: true,
    allow_google_signals: false, // No cross-device profiling, no ad audiences
    allow_ad_personalization_signals: false,
  });
};

/**
 * Withdrawing consent has to stop the script that is already on the page, not
 * just our own calls into it - the flag below is Google's documented opt-out.
 */
export const disableGA = () => {
  if (typeof window === 'undefined' || !GA_MEASUREMENT_ID) {
    return;
  }
  (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
};

// Check if analytics consent is given
const hasAnalyticsConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return isStorageAllowed('analytics');
};

// Track page view
export const trackPageView = (path: string) => {
  if (!hasAnalyticsConsent()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Page view (analytics not consented):', path);
    }
    return;
  }

  if (window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
    });
  } else if (process.env.NODE_ENV === 'development') {
    console.log('Page view:', path);
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  eventParams?: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  }
) => {
  if (!hasAnalyticsConsent()) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Event (analytics not consented):', eventName, eventParams);
    }
    return;
  }

  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  } else if (process.env.NODE_ENV === 'development') {
    console.log('Event:', eventName, eventParams);
  }
};

// Track button clicks
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent('button_click', {
    event_category: 'engagement',
    event_label: buttonName,
    button_name: buttonName,
    location: location || window.location.pathname,
  });
};

// Track donation button clicks
export const trackDonation = (amount?: number, source?: string) => {
  trackEvent('donation_click', {
    event_category: 'donation',
    event_label: source || 'donation_page',
    value: amount,
    donation_source: source || 'donation_page',
  });
};

// Track external link clicks
export const trackExternalLink = (url: string, linkText?: string) => {
  trackEvent('external_link_click', {
    event_category: 'outbound',
    event_label: linkText || url,
    link_url: url,
  });
};

// Track email link clicks
export const trackEmailClick = (email: string, context?: string) => {
  trackEvent('email_click', {
    event_category: 'contact',
    event_label: email,
    email_context: context || 'general',
  });
};
